/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Dropdown, FormControl, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {getPrefTariffCodes} from '../core/_requests'
import lodash from 'lodash'

type PrefTariffCodeDropdownProps = {
  onSelect: (code: string) => void
  disabled?: boolean
  value?: string
}

type PrefTariffCodeType = {
  id: string | number
  agreement_title: string
  code: string
}

const PrefTariffCodeDropdown: FC<PrefTariffCodeDropdownProps> = ({onSelect, disabled = false, value}) => {
  const axiosPrivate = useAxiosPrivate()
  const [codes, setCodes] = useState<Array<PrefTariffCodeType>>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedCode, setSelectedCode] = useState<PrefTariffCodeType>()
  const [offset, setOffset] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const limit = 50

  const fetchData = async (search: string, currentOffset: number, isAppending: boolean = false) => {
    try {
      setLoading(true)
      const res = await getPrefTariffCodes(axiosPrivate, {
        limit,
        offset: currentOffset,
        search,
      })

      const newCodes = res.data.data

      if (newCodes.length < limit) {
        setHasMore(false)
      } else {
        setHasMore(true)
      }

      if (isAppending) {
        setCodes((prev) => {
          const existingIds = new Set(prev.map((c) => c.id))
          const uniqueNewCodes = newCodes.filter((c: any) => !existingIds.has(c.id))
          return [...prev, ...uniqueNewCodes]
        })
      } else {
        setCodes(newCodes)
      }
    } catch (e) {
      console.error('Failed to fetch pref tariff codes', e)
    } finally {
      setLoading(false)
    }
  }

  const debouncedSearch = useCallback(
    lodash.debounce((search: string) => {
      setOffset(0)
      fetchData(search, 0, false)
    }, 500),
    []
  )

  useEffect(() => {
    fetchData('', 0, false)
  }, [])

  useEffect(() => {
    if (!value) {
      setSelectedCode(undefined)
      return
    }

    const found = codes.find((c) => c.code === value)
    if (found) {
      setSelectedCode(found)
    } else {
      if (!selectedCode || selectedCode.code !== value) {
        getPrefTariffCodes(axiosPrivate, {limit: 1, offset: 0, search: value})
          .then((res) => {
            if (res.data.data && res.data.data.length > 0) {
              const exactMatch = res.data.data.find((c: any) => c.code === value)
              if (exactMatch) {
                setSelectedCode(exactMatch)
              } else {
                setSelectedCode({id: '', code: value, agreement_title: ''})
              }
            } else {
              setSelectedCode({id: '', code: value, agreement_title: ''})
            }
          })
          .catch((e) => {
            console.error('Failed to fetch selected code', e)
            setSelectedCode({id: '', code: value, agreement_title: ''})
          })
      }
    }
  }, [value, codes])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    debouncedSearch(value)
  }

  const handleLoadMore = (e: React.MouseEvent) => {
    e.stopPropagation()
    const nextOffset = offset + limit
    setOffset(nextOffset)
    fetchData(searchTerm, nextOffset, true)
  }

  const handleSelect = (codeItem: PrefTariffCodeType) => {
    onSelect(codeItem.code)
    setSelectedCode(codeItem)
  }

  const handleToggle = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
    if (!isOpen && searchTerm !== '') {
      setSearchTerm('')
      setOffset(0)
      fetchData('', 0, false)
    }
  }

  const dropdownItem = codes.map((codeItem) => {
    return (
      <Dropdown.Item
        key={`${codeItem.id}-${codeItem.code}`}
        role='menuitem'
        size='sm'
        onClick={() => handleSelect(codeItem)}
        title={`${codeItem.agreement_title} - (${codeItem.code})`}
        style={{whiteSpace: 'normal'}}
      >
        {`${codeItem.agreement_title} - (${codeItem.code})`}
      </Dropdown.Item>
    )
  })

  return (
    <Dropdown onToggle={handleToggle} show={isDropdownOpen}>
      <Dropdown.Toggle
        variant='label-dropdown'
        id=''
        className='btn btn-sm btn-bold dropdown-toggle fw-normal text-start w-100'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '44px',
          height: 'auto',
          whiteSpace: 'normal',
        }}
        disabled={disabled}
      >
        {selectedCode ? (
          <div
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {`${selectedCode.agreement_title} - (${selectedCode.code})`}
          </div>
        ) : (
          'Select Code'
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className='dropdown-econ-menu w-100'
        style={{maxHeight: '300px', overflowY: 'auto'}}
      >
        <div
          style={{
            padding: '4px 8px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
          }}
        >
          <FormControl
            type='text'
            size='sm'
            placeholder='Search code...'
            className='dropdown-econ-search-bar'
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading && offset === 0 ? (
          <div className='d-flex justify-content-center p-3'>
            <Spinner animation='border' size='sm' variant='primary' />
          </div>
        ) : (
          <>
            {codes.length === 0 && !loading && (
              <div className='text-muted text-center p-2' style={{fontSize: '0.85rem'}}>
                No results found in database.
              </div>
            )}
            {dropdownItem}
            {hasMore && codes.length > 0 && (
              <div className='text-center p-2'>
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? <Spinner animation='border' size='sm' /> : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default PrefTariffCodeDropdown
